/**
 * PaymentRedirectPage - Handles payment redirect from tracking page
 * Creates Stripe checkout session and redirects user
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, AlertTriangle, CreditCard } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

const PaymentRedirectPage = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initiatePayment = async () => {
      if (!bookingId) {
        setError('ID de réservation manquant');
        setLoading(false);
        return;
      }

      try {
        // Create Stripe checkout session
        const response = await axios.post(`${API_URL}/api/payments/checkout`, {
          booking_id: bookingId,
          origin_url: window.location.origin
        });

        // Redirect to Stripe Checkout
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else {
          setError('Impossible de créer la session de paiement');
          setLoading(false);
        }
      } catch (err) {
        console.error('Payment error:', err);
        setError(err.response?.data?.detail || 'Erreur lors de l\'initialisation du paiement');
        setLoading(false);
      }
    };

    initiatePayment();
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4">
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {loading ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C9A24D]/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
              </div>
              <h2 className="text-xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
                Redirection vers le paiement
              </h2>
              <p className="text-[#E5E5E5]/60 text-sm">
                Veuillez patienter, vous allez être redirigé vers notre partenaire de paiement sécurisé...
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 text-[#E5E5E5]/40 text-xs">
                <CreditCard className="w-4 h-4" />
                <span>Paiement sécurisé par Stripe</span>
              </div>
            </>
          ) : error ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
                Erreur de paiement
              </h2>
              <p className="text-red-400/80 text-sm mb-6">
                {error}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B]"
                >
                  Réessayer
                </Button>
                <Link to="/suivi" className="flex-1">
                  <Button
                    variant="outline"
                    className="w-full border-[#C9A24D]/50 text-[#C9A24D] hover:bg-[#C9A24D]/10"
                  >
                    Retour au suivi
                  </Button>
                </Link>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentRedirectPage;
